/* LMC Orlando — White-Glove delivery fee sync.

   The PDP / Quick View delivery picker writes a "Delivery: White-Glove"
   line-item property on chair lines. This module keeps a hidden $275 fee
   product (Theme settings → Cart → "White-glove fee product", exposed as
   window.LMC_WG_FEE by theme.liquid) in the cart in lockstep with that
   property, so cart and checkout totals include the fee:

   - the fee line's quantity is pinned to the TOTAL number of chairs on
     White-Glove lines (the fee is charged per chair: 3 chairs with
     white-glove = 3 × $275), tracking quantity changes automatically
   - no chair line carries White-Glove → the fee line is removed

   Sync runs on page load and on every PUB_SUB_EVENTS.cartUpdate this
   module didn't cause itself (product-form adds, cart page qty changes).

   Latency: the sync is inherently one step BEHIND the user's action
   (Dawn's request finishes, then ours runs). To keep that gap to a
   single round trip, mutations ask Shopify for the re-rendered sections
   in the SAME request (the `sections` param on /cart/add.js and
   /cart/change.js) and swap them into the DOM directly — no follow-up
   section fetch, no cartUpdate re-publish for cart.js to react to. When
   the triggering event already carries the full cart state (change.js
   responses do), the initial /cart.js fetch is skipped too. */

(function () {
  'use strict';

  const cfg = window.LMC_WG_FEE;
  if (!cfg || !cfg.variantId || !window.routes) return;

  const FEE_PROP = '_lmc_wg_fee';
  /* Computed per mutation: the drawer section only exists in the DOM when
     Theme settings → Cart → Type is "drawer" (this store ships with
     "notification"), but if a merchant flips that toggle the drawer must
     stay in lockstep too — it only re-renders via cartUpdate broadcasts,
     which this module no longer emits after its own mutations. */
  const renderSectionNames = () => {
    const names = ['main-cart-items', 'cart-icon-bubble'];
    if (document.querySelector('cart-drawer')) names.push('cart-drawer');
    return names;
  };
  let syncing = false;

  const fetchCart = () => fetch(routes.cart_url + '.js').then((r) => r.json());

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => {
      if (!r.ok) throw new Error('[lmc-wg-fee] ' + url + ' -> ' + r.status);
      return r.json();
    });

  const isFeeLine = (item) => item.product_id === cfg.productId;
  const wgChairLines = (cart) =>
    cart.items.filter((item) => !isFeeLine(item) && item.properties && item.properties.Delivery === 'White-Glove');
  const wgChairCount = (cart) => wgChairLines(cart).reduce((sum, item) => sum + item.quantity, 0);

  /* A change.js response IS the full cart object; an add.js response only
     lists the added items. Only trust payloads that look like the former. */
  const isFullCart = (data) => !!(data && data.token && Array.isArray(data.items) && typeof data.item_count === 'number');

  /* Swap the section HTML returned alongside a mutation into the page:
     cart badge everywhere; line items + sticky summary on the cart page.
     Mirrors what cart.js does for its own mutations. */
  function renderSections(sections) {
    if (!sections) return;
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    if (sections['cart-icon-bubble']) {
      const target = document.getElementById('cart-icon-bubble');
      const source = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (target && source) target.innerHTML = source.innerHTML;
    }
    if (sections['cart-drawer']) {
      // Mirror CartItems.onCartUpdate's drawer branch (assets/cart.js):
      // replace the items list and the footer (which holds the totals).
      const html = parse(sections['cart-drawer']);
      ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
        const target = document.querySelector(selector);
        const source = html.querySelector(selector);
        if (target && source) target.replaceWith(source);
      });
    }
    if (sections['main-cart-items'] && document.getElementById('main-cart-items')) {
      const html = parse(sections['main-cart-items']);
      const itemsTarget = document.querySelector('cart-items');
      const itemsSource = html.querySelector('cart-items');
      if (!itemsTarget && itemsSource) {
        // The page is showing the empty-cart template but the cart just
        // gained items (e.g. a Quick View add from the empty-cart
        // recommendations) — there's no line-item markup to swap into, so
        // reload into the full template. Mirror of reloadIfCartPageEmptied.
        window.location.reload();
        return;
      }
      if (itemsTarget && itemsSource) itemsTarget.innerHTML = itemsSource.innerHTML;
      const summaryWrap = document.getElementById('lmc-cart-summary');
      const summaryTarget = summaryWrap && summaryWrap.querySelector('.js-cart-summary-contents');
      const summarySource = html.querySelector('#lmc-cart-summary .js-cart-summary-contents');
      if (summaryTarget && summarySource) summaryTarget.innerHTML = summarySource.innerHTML;
    }
  }

  const withSections = (body) =>
    Object.assign({}, body, { sections: renderSectionNames(), sections_url: routes.cart_url });

  /* Removing the last chair leaves the fee as the only line; after we
     delete it too, the cart page would render a stale fee row because the
     empty-cart section markup has none of the swap targets cart.js
     expects. Mirror cart.js's own empty-cart behavior and reload. */
  function reloadIfCartPageEmptied(state) {
    if (state.item_count === 0 && document.getElementById('main-cart-items')) {
      window.location.reload();
      return true;
    }
    return false;
  }

  async function sync(knownCart) {
    // Deliberate tradeoff: a cartUpdate arriving mid-sync is dropped, not
    // queued — any state it would have fixed self-heals on the next cart
    // mutation or page load, and queuing would risk request pile-up.
    if (syncing) return;
    syncing = true;
    try {
      const cart = isFullCart(knownCart) ? knownCart : await fetchCart();
      const feeLine = cart.items.find(isFeeLine);
      const wanted = wgChairCount(cart); // one fee unit per white-glove chair

      if (wanted > 0 && !feeLine) {
        const properties = {};
        properties[FEE_PROP] = 'auto';
        const state = await postJSON(
          routes.cart_add_url + '.js',
          withSections({ items: [{ id: cfg.variantId, quantity: wanted, properties: properties }] })
        );
        renderSections(state.sections);
      } else if (wanted === 0 && feeLine) {
        const state = await postJSON(routes.cart_change_url + '.js', withSections({ id: feeLine.key, quantity: 0 }));
        if (reloadIfCartPageEmptied(state)) return;
        renderSections(state.sections);
      } else if (feeLine && feeLine.quantity !== wanted) {
        const state = await postJSON(
          routes.cart_change_url + '.js',
          withSections({ id: feeLine.key, quantity: wanted })
        );
        renderSections(state.sections);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[lmc-wg-fee]', err);
    } finally {
      syncing = false;
    }
  }

  /* Explicit fee removal = "switch my order to curbside". Shares no mutex
     with sync(): a sync in flight when Remove is clicked can briefly
     conflict (same accepted tradeoff as dropped concurrent events — the
     window needs a cart mutation to land mid-click, and the next
     mutation/page load self-heals the state). */
  async function switchToCurbside(button) {
    button.disabled = true;
    try {
      const cart = await fetchCart();
      for (const line of wgChairLines(cart)) {
        // /cart/change.js replaces the whole properties object — merge so
        // any other properties on the line survive the flip.
        const properties = Object.assign({}, line.properties, { Delivery: 'Curbside' });
        await postJSON(routes.cart_change_url + '.js', { id: line.key, properties: properties });
      }
      const feeLine = cart.items.find(isFeeLine);
      let state = null;
      if (feeLine) {
        state = await postJSON(routes.cart_change_url + '.js', withSections({ id: feeLine.key, quantity: 0 }));
        if (reloadIfCartPageEmptied(state)) return;
      }
      if (state) {
        renderSections(state.sections);
      } else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // No fee line existed (property flips only) — let cart.js re-render.
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lmc-wg-fee' });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[lmc-wg-fee]', err);
      button.disabled = false;
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-lmc-wg-remove]');
    if (!button) return;
    event.preventDefault();
    switchToCurbside(button);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'lmc-wg-fee') return;
      sync(event && event.cartData);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync());
  } else {
    sync();
  }
})();
